<template>
  <div>
    <fuel-consumption-common-dialog
      :accept="accept"
      :cancel="close"
      :close="close"
      :showDialog="showDialog"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :title="title"
    >
      <template v-slot:contents>
        <!-- 年月切り替えスイッチ -->
        <div id="switch-button" class="flex mb-4">
          <div class="switch-item" :class="{ selected: isMonthly }">
            <div class="switch-item-label" @click="onClickSwitchButton()">
              <div class="flex p-3">
                <span>{{ monthText }}</span>
              </div>
            </div>
          </div>
          <div class="switch-item" :class="{ selected: !isMonthly }">
            <div class="switch-item-label" @click="onClickSwitchButton()">
              <div class="flex p-3">
                <span>{{ yearText }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 年度選択プルダウン -->
        <div v-if="!isMonthly" class="mb-4">
          <v-select
            class="w-full"
            v-model="selectedFiscalYear"
            :options="fiscalYears"
            :clearable="false"
          />
        </div>
        <!-- 月選択プルダウン -->
        <div v-else>
          <div class="mb-4">
            <v-select
              class="w-full"
              v-model="selectedYear"
              :options="years"
              :clearable="false"
            />
          </div>
          <div class="mb-4">
            <v-select
              class="w-full"
              v-model="selectedMonth"
              :options="filteredMonths"
              :clearable="false"
            />
          </div>
        </div>
      </template>
    </fuel-consumption-common-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { ColorMixin } from "@/mixins";
import FuelConsumptionCommonDialog from "./FuelConsumptionCommonDialog.vue";
import moment from "moment";
import vSelect from "vue-select";

const { mapActions } = createNamespacedHelpers("fuelConsumption");

const VesselState = {
  underway: "航行中",
  anchorage: "停泊中",
};

export default {
  mixins: [ColorMixin],
  components: { FuelConsumptionCommonDialog, vSelect },
  data() {
    return {
      error: { message: null },
      showDialog: false,
      title: "月・年次の選択",
      monthText: "月",
      yearText: "年",
      acceptText: "ダウンロード",
      cancelText: "キャンセル",
      vesselId: null,
      vesselName: null,
      isMonthly: true,
      selectedFiscalYear: null,
      selectedYear: null,
      selectedMonth: null,
      fiscalYears: [],
      years: [],
      months: [],
    };
  },
  created() {
    this.generateFiscalYears();
    this.generateYears();
    this.generateMonths();
  },
  watch: {
    selectedYear(newYear) {
      if (newYear === null) return;

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      if (newYear.value === currentYear) {
        this.selectedMonth = this.months[this.months.length - currentMonth];
      } else {
        this.selectedMonth = this.months[0];
      }
    },
  },
  computed: {
    filteredMonths() {
      if (this.selectedYear === null) return;

      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear.value === currentYear) {
        return this.months.filter(month => month.value <= currentMonth);
      }

      return this.months;
    },
  },
  methods: {
    ...mapActions(["fetchAllVoyageList", "fetchCsv", "fetchCsvAnchor"]),

    onClickSwitchButton() {
      this.isMonthly = !this.isMonthly;
    },

    accept() {
      let fileNameUnderway = this.generateFileName(VesselState.underway);
      let fileNameAnchorage = this.generateFileName(VesselState.anchorage);

      if (this.isMonthly) {
        this.downloadCsvByMonth(fileNameUnderway, VesselState.underway);
        this.downloadCsvByMonth(fileNameAnchorage, VesselState.anchorage);
      } else {
        this.downloadCsvByFiscalYear(fileNameUnderway, true);
        this.downloadCsvByFiscalYear(fileNameAnchorage, false);
      }
      this.close();
    },

    generateFileName(vesselState) {
      if (this.isMonthly) {
        return `${this.selectedYear.value}年${this.selectedMonth.value}月_燃料消費量_${vesselState}_${this.vesselName}.csv`;
      } else {
        return `${this.selectedFiscalYear.id}年度_燃料消費量_${vesselState}_${this.vesselName}.csv`;
      }
    },

    notifyDownloadStart(vesselState) {
      let periodText;
      if (this.isMonthly) {
        periodText = `${this.selectedYear.value}年${this.selectedMonth.value}月`;
      } else {
        periodText = `${this.selectedFiscalYear.id}年度`;
      }
      this.$vs.notify({
        title: `ダウンロード開始`,
        text: `${this.vesselName}の${vesselState}燃料消費量データ(${periodText})のCSV生成を要求しました。ファイルのダウンロードに時間がかかる場合があります。`,
        color: "warning",
        iconPack: "feather",
        icon: "icon-download",
        fixed: true,
      });
    },

    async downloadCsvByMonth(fileName, vesselState) {
      try {
        let from = moment()
          .year(this.selectedYear.value)
          .month(this.selectedMonth.value - 1)
          .startOf("month")
          .format("YYYY-MM-DD");

        let to = moment()
          .year(this.selectedYear.value)
          .month(this.selectedMonth.value - 1)
          .endOf("month")
          .format("YYYY-MM-DD");

        const payload = {
          vesselId: this.vesselId,
          from: from,
          to: to,
        };

        if (vesselState === VesselState.underway) {
          this.notifyDownloadStart(VesselState.underway);
          let csv = await this.fetchCsv(payload);
          await this.onCompleteDownload(csv, fileName);
        } else {
          this.notifyDownloadStart(VesselState.anchorage);
          let csv = await this.fetchCsvAnchor(payload);
          await this.onCompleteDownload(csv, fileName);
        }
      } catch (error) {
        this.$vs.notify({
          title: `${fileName}のダウンロードに失敗しました。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      }
    },

    async downloadCsvByFiscalYear(fileName, vesselState) {
      try {
        const payload = {
          vesselId: this.vesselId,
          from: this.selectedFiscalYear.value.fiscalYearStart,
          to: this.selectedFiscalYear.value.fiscalYearEnd,
        };
        if (vesselState === VesselState.underway) {
          this.notifyDownloadStart(VesselState.underway);
          let csv = await this.fetchCsv(payload);
          await this.onCompleteDownload(csv, fileName);
        } else {
          this.notifyDownloadStart(VesselState.anchorage);
          let csv = await this.fetchCsvAnchor(payload);
          await this.onCompleteDownload(csv, fileName);
        }
      } catch (error) {
        this.$vs.notify({
          title: `${fileName}のダウンロードに失敗しました。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      }
    },

    saveFile(csv, fileName) {
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const blob = new Blob([bom, csv], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
    },

    async onCompleteDownload(csv, fileName) {
      await this.saveFile(csv, fileName);
      this.$vs.notify({
        title: `ダウンロード完了`,
        text: `${fileName}のダウンロードが完了しました。`,
        color: "success",
        iconPack: "feather",
        icon: "icon-check",
        fixed: true,
      });
    },

    generateFiscalYears() {
      const nowYear = moment()
        .subtract(3, "months")
        .year();

      let options = [];
      for (let step = 0; step < 5; step++) {
        let year = nowYear - step;
        let fiscalYearStart = moment()
          .year(year)
          .month(3)
          .startOf("month")
          .format("YYYY-MM-DD");

        let fiscalYearEnd = moment()
          .year(year + 1)
          .month(2)
          .endOf("month")
          .format("YYYY-MM-DD");

        let op = {
          id: year,
          label: `${year}年度`,
          value: {
            fiscalYearStart: fiscalYearStart,
            fiscalYearEnd: fiscalYearEnd,
          },
        };
        options.push(op);
      }

      this.fiscalYears = options.map(el => {
        let isFiscalYear = moment().isBetween(
          el.value.fiscalYearStart,
          el.value.fiscalYearEnd,
          null,
          "[]"
        );
        if (isFiscalYear) {
          el.label = `今年度 (${el.label})`;
        }
        return el;
      });
    },

    generateYears() {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 5; i++) {
        const yearValue = currentYear - i;
        const label =
          yearValue === currentYear
            ? `今年（${yearValue}年）`
            : `${yearValue}年`;
        this.years.push({ value: yearValue, label: label });
      }
      this.years.sort((a, b) => b.value - a.value);
      this.selectedYear = this.years[0];
    },

    generateMonths() {
      for (let i = 12; i >= 1; i--) {
        this.months.push({ value: i, label: `${i}月` });
      }
      this.selectedMonth = this.months[0];
    },

    async show(vesselId, vesselName) {
      try {
        this.vesselId = vesselId;
        this.vesselName = vesselName;
        this.selectedFiscalYear = this.fiscalYears[0];
        this.selectedYear = this.years[0];
        this.showDialog = true;
      } catch (error) {
        let msg = error.msg;
        if (!msg) {
          msg = "予期せぬエラーが発生しました。";
        }
        this.$vs.notify({
          title: msg,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
        this.showDialog = false;
        this.vesselId = null;
      }
    },
    close() {
      this.showDialog = false;
      this.vesselId = null;
      this.selectedFiscalYear = null;
      this.selectedYear = null;
      this.selectedMonth = null;
      this.isMonthly = true;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";
</style>
